import React, { useState, useEffect, useRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import {
  tabbingOutsideOfElement,
  isKeyboardEvent,
  RealOrSyntheticEvent,
  elementOrDescendentHasFocus,
} from '@chegg-ui/utils'

export interface VisibleOnHoverWrapperProps {
  dismissible: React.RefObject<HTMLElement | HTMLDivElement>
  showIfDismissed?: React.RefObject<HTMLElement | HTMLDivElement>
  children: React.ReactElement
  className?: string
}

const Container = styled.span`
  position: relative;
`

const VisibleOnHoverWrapper = React.forwardRef(
  (
    { dismissible, showIfDismissed, children, className }: VisibleOnHoverWrapperProps,
    ref: React.Ref<HTMLDivElement | null>
  ) => {
    const wrapperRef: React.RefObject<HTMLDivElement> = useRef(null)
    /* istanbul ignore next */
    useImperativeHandle(ref, () => wrapperRef.current)
    const [isShown, setIsShown] = useState(false)
    const showDismissible = (): void => setIsShown(true)
    const givenOnMouseMove = children.props.onMouseMove
    const givenOnFocus = children.props.onFocus
    // event can either be a React synthetic event (from user interaction) or a "real" event (from the onclick listeners)
    const hideDismissible = (e: RealOrSyntheticEvent): void => {
      if (isKeyboardEvent(e)) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          setIsShown(false)
        }
        if (wrapperRef.current && tabbingOutsideOfElement(e, wrapperRef.current)) {
          setIsShown(false)
        }
      } else if (e.type === 'mouseleave') {
        setIsShown(false)
      }
    }

    useEffect(() => {
      if (isShown) {
        document.addEventListener('click', hideDismissible)
        document.addEventListener('keydown', hideDismissible)
      }
      return () => {
        document.removeEventListener('click', hideDismissible)
        document.removeEventListener('keydown', hideDismissible)
      }
    }, [isShown])

    useEffect(() => {
      if (dismissible.current !== null) {
        dismissible.current.style.display = isShown ? 'block' : 'none' // eslint-disable-line no-param-reassign
      }
    }, [dismissible, isShown])

    useEffect(() => {
      if (showIfDismissed && showIfDismissed.current !== null) {
        showIfDismissed.current.style.display = isShown ? 'none' : 'block' // eslint-disable-line no-param-reassign
      }
    }, [showIfDismissed, isShown])

    return (
      <Container
        onMouseLeave={(e) => {
          /* istanbul ignore if */
          if (!wrapperRef.current) {
            hideDismissible(e as React.MouseEvent<HTMLSpanElement, MouseEvent>)
            // we don't want to hide the dismissible immediately if it has keyboard focus
          } else if (!elementOrDescendentHasFocus(wrapperRef.current)) {
            hideDismissible(e as React.MouseEvent<HTMLSpanElement, MouseEvent>)
          }
        }}
        className={className}
        ref={wrapperRef}
      >
        {React.cloneElement(children, {
          onMouseMove: (e: React.MouseEvent | React.KeyboardEvent) => {
            if (givenOnMouseMove) {
              givenOnMouseMove(e)
            }
            showDismissible()
          },
          onFocus: (e: React.MouseEvent | React.KeyboardEvent) => {
            if (givenOnFocus) {
              givenOnFocus(e)
            }
            showDismissible()
          },
          'aria-expanded': isShown,
        })}
      </Container>
    )
  }
)

export default VisibleOnHoverWrapper
